import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Loader2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDialogCloseGuard } from '@/hooks/useDialogCloseGuard';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useAnnouncementStore } from '../store';
import { announcementService } from '../services';
import { AnnouncementForm, createAnnouncementColumns } from '../components';
import type { AnnouncementDto, AnnouncementListItemDto } from '../types';
import type { AnnouncementFormData } from '../schemas';

export function AnnouncementsAdminPage() {
  usePageTitle('Announcements');

  const {
    allAnnouncements,
    isLoadingAll,
    allError,
    isSubmitting,
    submitError,
    fetchAll,
    create,
    update,
    remove,
    clearError,
  } = useAnnouncementStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AnnouncementDto | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<AnnouncementListItemDto | null>(null);

  // Track each form's dirty state so the close guard can prompt before discarding.
  const [createDirty, setCreateDirty] = useState(false);
  const [editDirty, setEditDirty] = useState(false);

  const createGuard = useDialogCloseGuard({
    isDirty: createDirty,
    onClose: () => {
      setCreating(false);
      setCreateDirty(false);
    },
  });
  const editGuard = useDialogCloseGuard({
    isDirty: editDirty,
    onClose: () => {
      setEditing(null);
      setEditDirty(false);
    },
  });

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // M3 (86d44z1xm) — the shell's "Post announcement" quick action. There is no composer ROUTE:
  // the composer is the Dialog below, driven by this page's own `creating` state, so the shortcut
  // arrives as a one-shot intent marker instead. Stripped immediately with `replace: true` so a
  // refresh or a back-navigation does not re-open the dialog over the user's work.
  // Same mechanism as `?new=1` at QuotationWorkspacePage.tsx:186.
  useEffect(() => {
    if (searchParams.get('action') !== 'new') return;
    clearError();
    setCreating(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, clearError]);

  // When opening edit dialog, fetch full row (admin list omits content for size).
  async function handleEdit(id: number) {
    try {
      const full = await announcementService.getById(id);
      setEditing(full);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load announcement');
    }
  }

  async function handleCreateSubmit(data: AnnouncementFormData) {
    const ok = await create({
      title: data.title,
      content: data.content,
      priority: data.priority,
      status: data.status,
      isPinned: data.isPinned,
      publishAt: data.publishAt,
      expiresAt: data.expiresAt,
    });
    if (ok) {
      toast.success('Announcement created');
      setCreateDirty(false);
      setCreating(false);
    } else if (submitError) {
      toast.error(submitError);
    }
  }

  async function handleUpdateSubmit(data: AnnouncementFormData) {
    if (!editing) return;
    const ok = await update(editing.id, {
      title: data.title,
      content: data.content,
      priority: data.priority,
      status: data.status,
      isPinned: data.isPinned,
      publishAt: data.publishAt,
      expiresAt: data.expiresAt,
    });
    if (ok) {
      toast.success('Announcement updated');
      setEditDirty(false);
      setEditing(null);
    } else if (submitError) {
      toast.error(submitError);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteCandidate) return;
    const ok = await remove(deleteCandidate.id);
    if (ok) {
      toast.success('Announcement deleted');
      setDeleteCandidate(null);
    } else if (submitError) {
      toast.error(submitError);
    }
  }

  const columns = useMemo(
    () =>
      createAnnouncementColumns({
        onEdit: handleEdit,
        onDelete: (id) => {
          const row = allAnnouncements.find((a) => a.id === id);
          if (row) setDeleteCandidate(row);
        },
      }),
    [allAnnouncements] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const table = useReactTable({
    data: allAnnouncements,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Announcements"
        description="Publish formatted notices to all DOP users. Critical-priority items show as a modal at login; others appear on Home."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => fetchAll()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <PermissionGuard permission={PERMISSIONS.ANNOUNCEMENT_CREATE}>
              <Button
                size="sm"
                onClick={() => {
                  clearError();
                  setCreating(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                New announcement
              </Button>
            </PermissionGuard>
          </>
        }
      />

      {isLoadingAll && allAnnouncements.length === 0 ? (
            <TableSkeleton rows={8} cols={6} />
          ) : allError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12">
              <p className="text-sm text-destructive">{allError}</p>
              <Button variant="outline" size="sm" onClick={() => fetchAll()}>
                Try again
              </Button>
            </div>
          ) : allAnnouncements.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
              <p className="text-sm font-medium">No announcements yet</p>
              <p className="text-sm text-muted-foreground">
                Create one to share with the team.
              </p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={createGuard.handleOpenChange}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          onPointerDownOutside={createGuard.preventOutsideClose}
          onInteractOutside={createGuard.preventOutsideClose}
        >
          <DialogHeader>
            <DialogTitle>New announcement</DialogTitle>
            <DialogDescription>
              Save as Draft to keep working, or set to Published to make it visible immediately.
            </DialogDescription>
          </DialogHeader>
          <AnnouncementForm
            onSubmit={handleCreateSubmit}
            onCancel={() => createGuard.handleOpenChange(false)}
            onDirtyChange={setCreateDirty}
            isSubmitting={isSubmitting}
            submitLabel="Create"
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={editGuard.handleOpenChange}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          onPointerDownOutside={editGuard.preventOutsideClose}
          onInteractOutside={editGuard.preventOutsideClose}
        >
          <DialogHeader>
            <DialogTitle>Edit announcement</DialogTitle>
            <DialogDescription>
              Update content or change the status to publish or archive.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <AnnouncementForm
              initial={editing}
              onSubmit={handleUpdateSubmit}
              onCancel={() => editGuard.handleOpenChange(false)}
              onDirtyChange={setEditDirty}
              isSubmitting={isSubmitting}
              submitLabel="Save changes"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteCandidate}
        onOpenChange={(o) => !o && setDeleteCandidate(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete announcement?</DialogTitle>
            <DialogDescription>
              "{deleteCandidate?.title}" will be hidden from all lists. This cannot be undone
              from the UI.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteCandidate(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
